"use client";

import { useEffect, useRef, useState } from "react";
import { isFilled, asLink } from "@prismicio/client";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import '@splidejs/react-splide/css/core';
import "./carrousel-slider.css";
import { PrismicImage, PrismicLink, PrismicRichText } from "@prismicio/react";


const CardDefault = ({ category, isCarousel, index }) => {
  const hasLink = isFilled.link(category.cta);
  const linkProps = {};
  if (hasLink) {
    linkProps.field = category.cta;
  }

  let Wrapper = 'div';
  if (hasLink) {
    Wrapper = PrismicLink;
  } else {
    Wrapper = 'div';
  }

  console.log('CATEGORY', category, isCarousel);
  return (
    <Wrapper
      {...linkProps}
      className={`p-4 overflow-hidden rounded-2xl flex flex-col gap-3 w-full ${index % 2 === 0 ? 'bg-moyam-orange text-beige' : 'bg-beige text-moyam-orange'}`}>
      <div className="w-full h-full overflow-hidden rounded-2xl ">
        <PrismicImage field={category.img} className={`transition duration-300 h-65 md:h-[340px] w-full object-cover ${isCarousel ? "group-[&.is-visible:hover]:scale-115" : "group-hover:scale-115"}`} />
      </div>
      <h3 className={`h4 py-1 leading-5 md:text-2xl font-bold line-clamp-3 md:leading-6`}>
        {category.title}
      </h3>
    </Wrapper>
  );
};


/**
 * @typedef {import("@prismicio/client").Content.CarrouselSlice} CarrouselSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<CarrouselSlice>} CarrouselProps
 * @type {import("react").FC<CarrouselProps>}
 */
const CarouselCategories = ({ slice }) => {
  const categories = slice.primary.categories || [];
  const [deviceType, setDeviceType] = useState("desktop");

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 768) {
        setDeviceType("mobile");
      } else if (width >= 768 && width < 1110) {
        setDeviceType("tablet");
      } else if (width >= 1110 && width < 1600) {
        setDeviceType("desktop");
      } else {
        setDeviceType("xlarge");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const showMobileSlider = deviceType === "mobile" && categories.length >= 1;
  const showTabletSlider = deviceType === "tablet" && categories.length > 1;
  const showDesktopSlider = deviceType === "desktop" && categories.length > 2;
  const showXlargeSlider = deviceType === "xlarge" && categories.length > 3;
  const showDesktopGrid = deviceType === "desktop" && categories.length <= 2;
  const showTabletGrid = deviceType === "tablet" && categories.length <= 1;
  const showXlargeGrid = deviceType === "xlarge" && categories.length <= 3;


  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`py-10 md:py-20 flex flex-col overflow-visible bg-dark-pink`}
    >
      <div className={`px-5 md:px-20 flex gap-6 text-beige items-center md:items-start 
        ${showDesktopSlider || showTabletSlider || showXlargeSlider ? 'justify-start' : 'justify-center'}`}>
        {isFilled.image(slice.primary.icon) && (
          <PrismicImage
            field={{
              ...slice.primary.icon,
              alt: slice.primary.icon.alt || "Icône d'illustration"
            }}
            className="h-[30px] md:h-[46.5px] w-auto"
          />
        )}

        {isFilled.richText(slice.primary.title) && (
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading2: ({ children }) => <h2 className={`h2 ${showDesktopSlider || showTabletSlider || showXlargeSlider ? 'pr-30' : ''}`}>{children}</h2>,
              heading3: ({ children }) => <h3 className={`h2 ${showDesktopSlider || showTabletSlider || showXlargeSlider ? 'pr-30' : ''}`}>{children}</h3>,
              heading4: ({ children }) => <h4 className={`h2 ${showDesktopSlider || showTabletSlider || showXlargeSlider ? 'pr-30' : ''}`}>{children}</h4>,
            }}
          />
        )}
      </div>
      {isFilled.group(slice.primary.categories) && (
        <>
          {showMobileSlider && (
            <div className="pt-6">
              <div className="flex px-5 gap-2 snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] overflow-x-scroll">
                {categories.map((category, index) => (
                  <div
                    key={index}
                    className={`snap-center snap-always flex-shrink-0 ${categories.length === 1 ? 'w-full' : 'w-[75vw]'} max-w-[360px]`}
                  >
                    <CardDefault
                      category={category}
                      isCarousel={false}
                      index={index}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {showDesktopSlider && (
            <div className="overflow-visible carrousel-recipes pt-6">
              <Splide
                options={{
                  perPage: 2,
                  perMove: 1,
                  gap: "1.5rem",
                  pagination: false,
                  arrows: true,
                  width: "100%",
                  padding: { right: "13.75rem", left: "5rem" },
                }}
                aria-label="Gammes"
              >
                {categories.map((category, index) => (
                  <SplideSlide key={index} className="group overflow-visible">
                    <CardDefault
                      category={category}
                      isCarousel={true}
                      index={index}
                    />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          )}

          {showXlargeSlider && (
            <div className="overflow-visible carrousel-recipes pt-6">
              <Splide
                options={{
                  perPage: 3,
                  perMove: 1,
                  gap: "1.5rem",
                  pagination: false,
                  arrows: true,
                  width: "100%",
                  padding: { right: "13.75rem", left: "5rem" },
                }}
                aria-label="Gammes"
              >
                {categories.map((category, index) => (
                  <SplideSlide key={index} className="group overflow-visible">
                    <CardDefault
                      category={category}
                      isCarousel={true}
                      index={index}
                    />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          )}

          {showTabletSlider && (
            <div className="overflow-visible carrousel-recipes pt-6">
              <Splide
                options={{
                  perPage: 1,
                  perMove: 1,
                  gap: "1.5rem",
                  pagination: false,
                  arrows: true,
                  width: "100%",
                  padding: { right: "5rem", left: "5rem" },
                }}
                aria-label="Gammes"
              >
                {categories.map((category, index) => (
                  <SplideSlide key={index} className="group overflow-visible">
                    <CardDefault
                      category={category}
                      isCarousel={true}
                      index={index}
                    />
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          )}

          {showDesktopGrid && (
            <div className="px-20 flex flex-row justify-center gap-6 pt-6">
              {categories.map((category, index) => (
                <div key={index} className="group w-1/2 max-w-[540px]">
                  <CardDefault
                    category={category}
                    isCarousel={false}
                    index={index}
                  />
                </div>
              ))}
            </div>
          )}

          {showXlargeGrid && (
            <div className="px-20 flex flex-row justify-center gap-6 pt-6">
              {categories.map((category, index) => (
                <div key={index} className="group w-1/2 max-w-[540px]">
                  <CardDefault
                    category={category}
                    isCarousel={false}
                    index={index}
                  />
                </div>
              ))}
            </div>
          )}

          {showTabletGrid && (
            <div className="px-20 flex flex-row justify-center pt-6">
              {categories.map((category, index) => (
                <div key={index} className="group w-full">
                  <CardDefault
                    category={category}
                    isCarousel={false}
                    index={index}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default CarouselCategories;
